import { FC } from 'react'
import { Button, styled } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload'
import { enqueueSnackbar } from 'notistack'
interface Props {
  wistiaId: string
  questionNumber?: number
}

const DownloadTranscriptButton: FC<Props> = ({ wistiaId, questionNumber }) => {
  const handleClick = async () => {
    try {
      const response = await fetch(`https://fast.wistia.com/embed/captions/${wistiaId}.vtt`)
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }
      const text = await response.text()
      if (!text) {
        throw new Error('Empty transcript')
      }
      const anchor = document.createElement('a')
      anchor.href = `data:text/vtt;charset=utf-8,${encodeURIComponent(text)}`
      anchor.download = 'transcript.vtt'
      anchor.type = 'text/vtt'
      anchor.click()
    } catch {
      enqueueSnackbar('Unable to retrieve the transcript. Please try again later.', { variant: 'error' })
    }
  }

  return (
    <StyledButton disableFocusRipple disableRipple onClick={handleClick} aria-label={`Download Transcript for Question ${questionNumber}`}>
      <DownloadIcon icon={faDownload} />
      <ButtonText>Download Transcript</ButtonText>
    </StyledButton>
  )
}

const StyledButton = styled(Button)(({ theme }) => ({
  alignSelf: 'flex-start',
  height: '30px',
  minWidth: 'fit-content',
  padding: theme.spacing(4, 3),
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '50px',
  textTransform: 'none',
  backgroundColor: theme.palette.blue[100],
  transition: theme.transitions.create(['background-color']),
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.blue[700],
    '& span, & svg': {
      color: theme.palette.common.white,
    },
  },
}))

const ButtonText = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  lineHeight: '1.0625rem',
  fontWeight: 500,
  color: theme.palette.blue[800],
  margin: theme.spacing(0),
  transition: theme.transitions.create(['color']),
}))

const DownloadIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.blue[800],
  transition: theme.transitions.create(['color']),
}))

export default DownloadTranscriptButton
